/* ============================================================
 * GoodBuyz Main Navigation — progressive enhancement
 *
 * The nav renders and functions without this file. Markup is
 * server-rendered Liquid and the dropdowns are <details> elements, so
 * open/close, keyboard access and no-JS all work already.
 *
 * This adds only what CSS cannot:
 *   - the 400ms pause between click and open (DEV NOTE D1)
 *   - closing an open dropdown when another one opens
 *   - click-outside and Escape to close dropdowns
 *
 * Note the deliberate asymmetry with the mega menu: DEV NOTE D6 says the
 * mega menu may ONLY be closed by its two X buttons, so it is excluded
 * from the click-outside handler here. gb-shop-menu.js owns it.
 * ============================================================ */

(function () {
  'use strict';

  function init() {
    var nav = document.querySelector('[data-gb-main-nav]');
    if (!nav) return;

    var delay = 400;
    if (nav.dataset.gbOpenDelay) {
      var parsed = parseInt(nav.dataset.gbOpenDelay, 10);
      if (!isNaN(parsed)) delay = parsed;
    }

    var details = Array.prototype.slice.call(nav.querySelectorAll('[data-gb-dropdown]'));
    /* No early return on an empty `details` list: a bar with only a mega
       block and plain links still needs its open-state tracked, or the
       division hairline never appears. */

    var openTimer = null;

    var bar = nav.closest('.gb-main-nav-bar') || nav;

    function anyOpen() {
      if (details.some(function (d) { return d.open; })) return true;
      var t = nav.querySelector('.gb-shop-trigger[aria-expanded="true"]');
      return !!t;
    }

    // The bar's lower hairline becomes the division between the bar and the
    // open panel, so it needs to know when anything is open.
    function syncBarState() {
      bar.classList.toggle('is-menu-open', anyOpen());
    }

    function closeAll(except) {
      details.forEach(function (d) {
        if (d !== except) d.open = false;
      });
      syncBarState();
    }

    /* Hover-to-switch across the top bar has been REMOVED.
     *
     * It was added on 2026-08-25 by request, on top of dev note D3: once a menu
     * was open, moving across the bar switched to the hovered item. The design
     * has since moved to click everywhere, so D3 stands on its own again and
     * every dropdown now opens only from its summary, with the same 400ms pause.
     *
     * `data-gb-no-hover` on the grid panel existed solely to opt that panel out
     * of this behaviour. It is now inert but left in the markup: it costs
     * nothing, and removing it would touch a snippet for no functional gain. */

    // Keep the bar state in step with the mega menu, which gb-shop-menu.js owns.
    var megaTrigger = nav.querySelector('.gb-shop-trigger');
    if (megaTrigger && window.MutationObserver) {
      new MutationObserver(syncBarState).observe(megaTrigger, {
        attributes: true,
        attributeFilter: ['aria-expanded']
      });
    }

    details.forEach(function (d) {
      d.addEventListener('toggle', syncBarState);
    });

    details.forEach(function (d) {
      var summary = d.querySelector('summary');
      if (!summary) return;

      summary.addEventListener('click', function (e) {
        // Already open: let the browser close it immediately. Closing
        // should never feel delayed.
        if (d.open) return;

        // DEV NOTE D1 — 400ms pause between click and open.
        e.preventDefault();

        if (openTimer) clearTimeout(openTimer);
        openTimer = setTimeout(function () {
          closeAll(d);
          d.open = true;
        }, delay);
      });

      // Keyboard activation should not be delayed — it is already
      // deliberate, and a delay reads as unresponsiveness.
      summary.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          if (openTimer) clearTimeout(openTimer);
          closeAll(d);
        }
      });
    });

    // Click outside closes dropdowns. The mega menu is excluded on
    // purpose — see DEV NOTE D6 in the header comment.
    document.addEventListener('click', function (e) {
      if (nav.contains(e.target)) return;
      if (openTimer) clearTimeout(openTimer);
      closeAll(null);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;

      var open = details.filter(function (d) { return d.open; });
      if (!open.length) return;

      if (openTimer) clearTimeout(openTimer);
      closeAll(null);

      // Return focus so keyboard users are not stranded.
      var summary = open[0].querySelector('summary');
      if (summary) summary.focus();
    });

    /* ----------------------------------------------------------
     * Mobile drawer
     *
     * Ours, not the theme's. `.mobileMenu-toggle` and the handler at
     * assets/theme.js:575 open Ella's halo-sidebar; that button lives in
     * sections/header-mobile.liquid, which is deliberately disabled. So
     * nothing here contends with the theme, and the drawer renders from
     * the same section blocks as the desktop bar.
     * ---------------------------------------------------------- */
    /* ------------------------------------------------------------
     * Mobile tile grid — the "extra buttons" under the category list
     *
     * The tiles are the bar's non-category items re-laid-out, so their
     * labels, urls and icons live in the nav blocks. Liquid cannot read
     * them where the grid is rendered: for theme blocks section.blocks
     * returns opaque entries — type '@theme', every setting empty — so the
     * grid built itself from nothing and came out empty.
     *
     * Each block therefore renders its own tile into a <template> inside
     * its <li>, and this moves them into the grid's two columns. Liquid
     * still owns the markup; this only relocates it.
     *
     * Nothing is lost without JS: the drawer only opens by script, so a
     * browser that could not run this could never have seen the grid.
     * ---------------------------------------------------------- */
    var tiles = document.querySelector('[data-gb-tiles]');

    if (tiles) {
      var cols = {
        left: tiles.querySelector('[data-gb-tiles-col="left"]'),
        right: tiles.querySelector('[data-gb-tiles-col="right"]')
      };
      // Block tiles first, in document order; 'late' tiles (the wishlist, which
      // renders before the nav bar) after them, so they sit under the blocks.
      var sources = Array.prototype.slice.call(document.querySelectorAll('[data-gb-tile]:not([data-gb-tile-late])'))
        .concat(Array.prototype.slice.call(document.querySelectorAll('[data-gb-tile][data-gb-tile-late]')));
      var leftCount = parseInt(tiles.getAttribute('data-gb-left-count'), 10);
      if (isNaN(leftCount)) leftCount = 5;

      // The counter advances for AUTO tiles only. Pinning one tile right must
      // not push an auto tile across the boundary, or setting a single tile
      // would silently rearrange the others.
      var auto = 0;

      sources.forEach(function (tpl) {
        var col = tpl.getAttribute('data-gb-tile-col') || 'auto';
        var target;

        if (col === 'left' || col === 'right') {
          target = cols[col];
        } else {
          auto += 1;
          target = auto <= leftCount ? cols.left : cols.right;
        }

        if (target && tpl.content) target.appendChild(tpl.content.cloneNode(true));
      });

      // Rendered hidden so an empty grid does not leave a 106px gap under the
      // category list. It is only worth showing once something is in it.
      if (cols.left.children.length || cols.right.children.length) {
        tiles.hidden = false;
      }
    }

    var drawer = document.querySelector('[data-gb-drawer]');

    if (drawer) {
      var openers = document.querySelectorAll('[data-gb-drawer-open]');
      var lastOpener = null;

      function drawerOpen() { return drawer.classList.contains('is-open'); }

      /* D5 — lock the page behind the drawer.
       *
       * Just a class. The offset save/restore is GONE, along with the
       * `position: fixed` it existed to compensate for.
       *
       * That approach hid the drawer completely: a fixed ancestor becomes the
       * containing block for fixed descendants, so `.gb-drawer`'s `inset: 0`
       * resolved against the body box rather than the viewport, and the inline
       * `top: -<scrollY>px` then pushed the drawer that far above the screen. It
       * only looked correct at scroll position 0.
       *
       * `overflow: hidden` creates no containing block, and preserves the scroll
       * position by itself — so there is nothing to store and nothing to
       * restore. See the CSS for why touch-action carries the iOS case. */
      function lockPage(lock) {
        document.body.classList.toggle('gb-drawer-open', lock);
      }

      function setDrawer(open) {
        drawer.classList.toggle('is-open', open);
        drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
        lockPage(open);

        openers.forEach(function (btn) {
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        });

        if (open) {
          // Focus the close button so the drawer is immediately dismissable
          // from the keyboard, rather than leaving focus behind on the page.
          var close = drawer.querySelector('.gb-drawer__close');
          if (close) close.focus();
        } else if (lastOpener) {
          lastOpener.focus();
          lastOpener = null;
        }
      }

      openers.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          lastOpener = this;
          // Opening is immediate. The 400ms pause in dev note D1 is about
          // the desktop mega panel; a drawer that lags a tap reads as broken.
          setDrawer(true);
        });
      });

      drawer.querySelectorAll('[data-gb-drawer-close]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          setDrawer(false);
        });
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drawerOpen()) setDrawer(false);
      });

      // Keep focus inside the panel while it is open.
      drawer.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab' || !drawerOpen()) return;

        var panel = drawer.querySelector('.gb-drawer__panel');
        if (!panel) return;

        var focusable = panel.querySelectorAll(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;

        var first = focusable[0];
        var last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });

      // A resize up to desktop leaves the drawer stranded open over a layout
      // it was never meant for, and the body scroll lock with it.
      window.addEventListener('resize', function () {
        if (window.innerWidth > 991 && drawerOpen()) setDrawer(false);
      });
    }

    /* ----------------------------------------------------------
     * Mobile sticky "stuck" state
     *
     * DEV NOTE (mobile sticky): top/bottom padding reduces from 10px to 6px
     * once the header sticks, and the row compacts to hamburger + search icon
     * + logo + account + cart. position:sticky alone gives no state to hook,
     * so a scroll listener toggles `is-stuck` past a small threshold. The CSS
     * does the padding change. Since 2026-10-06 the persistent search row
     * stays visible while stuck (no swap to a search icon).
     *
     * TODO (with John): the SKIMS-style transition — a ~1s right-to-left fade
     * that then snaps the bar to the left to open the mega menu — hangs off
     * this same `is-stuck` class. The fade is in the stylesheet; the
     * "snap-left-opens-mega" behaviour is intentionally NOT wired yet.
     * ---------------------------------------------------------- */
    /* ----------------------------------------------------------
     * Sticky announcement offset
     *
     * The announcement is position:sticky at every width. Whatever docks
     * under it — the stuck mobile header, the desktop sticky bar and its mega
     * panel — reads --gb-ann-h: the announcement's live bottom edge, which is
     * its height while pinned and 0 once dismissed (the theme removes it).
     * ---------------------------------------------------------- */
    var syncAnnouncement = function () {
      var ann = document.querySelector('.shopify-section.gb-announcement');
      var bottom = ann ? Math.max(0, ann.getBoundingClientRect().bottom) : 0;
      document.documentElement.style.setProperty('--gb-ann-h', Math.round(bottom) + 'px');
    };
    var annRaf = null;
    var queueAnnouncement = function () {
      if (annRaf === null) annRaf = window.requestAnimationFrame(function () { annRaf = null; syncAnnouncement(); });
    };
    window.addEventListener('scroll', queueAnnouncement, { passive: true });
    window.addEventListener('resize', queueAnnouncement);
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-close-announcement]')) {
        setTimeout(function () { syncAnnouncement(); window.dispatchEvent(new Event('scroll')); }, 0);
      }
    }, true); // capture: the theme's close handler stops propagation
    syncAnnouncement();

    /* Viewport width WITHOUT the scrollbar, for anything that has to line up
     * with the screen edge from inside a padded container (the product-row
     * arrows). CSS 100vw includes the scrollbar. */
    var syncViewport = function () {
      document.documentElement.style.setProperty('--gb-vw', document.documentElement.clientWidth + 'px');
    };
    window.addEventListener('resize', syncViewport);
    syncViewport();

    var mheader = document.querySelector('[data-gb-mobile-header]');
    if (mheader) {
      /* The header cannot rely on position:sticky — its section wrapper is
       * barely taller than it, so it let go after one chip row. Stuck, it
       * turns position:fixed (CSS) under the sticky announcement bar, and a
       * spacer holds its natural height so the page does not jump. Only when
       * the section has "Stick the bar" on (gb-mheader--sticky). */
      var STUCK_AT = 40;
      var canStick = mheader.classList.contains('gb-mheader--sticky');
      var spacer = document.createElement('div');
      spacer.className = 'gb-mheader-spacer';
      spacer.setAttribute('aria-hidden', 'true');
      mheader.parentNode.insertBefore(spacer, mheader);

      var stuckRaf = null;
      var syncStuck = function () {
        stuckRaf = null;
        var mobile = window.innerWidth <= 991;
        var stuck = mobile && window.pageYOffset > STUCK_AT;

        if (stuck && !mheader.classList.contains('is-stuck')) {
          // Measure before it leaves the flow — this is the height to hold.
          spacer.style.height = (canStick ? mheader.offsetHeight : 0) + 'px';
        } else if (!stuck) {
          spacer.style.height = '0px';
        }
        mheader.classList.toggle('is-stuck', stuck);
        // Docks under the announcement via --gb-ann-h (set above).
        syncAnnouncement();
      };
      var queueStuck = function () {
        if (stuckRaf === null) stuckRaf = window.requestAnimationFrame(syncStuck);
      };
      window.addEventListener('scroll', queueStuck, { passive: true });
      window.addEventListener('resize', queueStuck);
      syncStuck();
    }

    /* ----------------------------------------------------------
     * Mobile header cart value
     *
     * The label under the cart icon shows the cart total. The theme already
     * rewrites [data-cart-count] after every cart change, so watching that
     * number (plus the cart events apps and the theme dispatch) is the one
     * hook that sees every add, change and remove. Each change re-reads
     * /cart.js and formats with the theme's own money format.
     * ---------------------------------------------------------- */
    var cartTotal = document.querySelector('[data-gb-cart-total]');
    if (cartTotal) {
      var cartTimer = null;
      var refreshTotal = function () {
        clearTimeout(cartTimer);
        cartTimer = setTimeout(function () {
          fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js', {
            credentials: 'same-origin',
            headers: { 'Accept': 'application/json' }
          })
            .then(function (res) { return res.ok ? res.json() : null; })
            .then(function (cart) {
              if (!cart) return;
              cartTotal.textContent = (window.Shopify && typeof window.Shopify.formatMoney === 'function')
                ? window.Shopify.formatMoney(cart.total_price, window.money_format)
                : (cart.total_price / 100).toFixed(2);
            })
            .catch(function () {});
        }, 150);
      };

      var countEl = mheader && mheader.querySelector('[data-cart-count]');
      if (countEl && 'MutationObserver' in window) {
        new MutationObserver(refreshTotal).observe(countEl, { childList: true, characterData: true, subtree: true });
      }
      ['cart-update', 'cart:refresh', 'cart:updated', 'cart:change'].forEach(function (name) {
        document.addEventListener(name, refreshTotal);
      });
    }

    /* ----------------------------------------------------------
     * Desktop condensed sticky bar
     *
     * The active header has no desktop sticky, so this is a purpose-built bar
     * (snippets/gb-sticky-desktop) that is fixed, hidden until scrolled, and
     * only shown above 991px. It reuses the real header's fixed overlays: its
     * search button triggers the header's own search <summary>, and its cart
     * link carries data-cart-sidebar so the theme opens the drawer.
     * ---------------------------------------------------------- */
    var dsticky = document.querySelector('[data-gb-desktop-sticky]');
    if (dsticky) {
      var DSTICKY_AT = 200;
      var dRaf = null;
      var syncDSticky = function () {
        dRaf = null;
        var on = window.innerWidth > 1024 && window.pageYOffset > DSTICKY_AT;
        dsticky.classList.toggle('is-visible', on);
        dsticky.setAttribute('aria-hidden', on ? 'false' : 'true');

        /* The sticky bar's Shop button opens the mega panel (it carries
         * .gb-shop-trigger, so gb-shop-menu.js binds it with no help from
         * here). But the panel is position:absolute against .gb-main-nav,
         * which is scrolled out of view by the time this bar appears — so it
         * would open off-screen.
         *
         * These two lines are what the stylesheet needs to fix that, and
         * neither is expressible in CSS: a body-level class, because the panel
         * lives inside the nav subtree and cannot see this bar's state; and
         * the bar's measured height, so the panel's `top` is a real
         * measurement rather than a constant that drifts the next time the
         * bar's padding changes. */
        document.body.classList.toggle('gb-dsticky-on', on);
        if (on) {
          document.documentElement.style.setProperty(
            '--gb-dsticky-h', dsticky.offsetHeight + 'px'
          );
        }
      };

      var scheduleDSticky = function () {
        if (dRaf === null) dRaf = window.requestAnimationFrame(syncDSticky);
      };
      window.addEventListener('scroll', scheduleDSticky, { passive: true });
      window.addEventListener('resize', scheduleDSticky, { passive: true });
      syncDSticky();

      dsticky.addEventListener('click', function (e) {
        if (!e.target.closest('[data-gb-dsticky-search]')) return;
        e.preventDefault();
        var summary = document.querySelector('.header-single-line .header__search-full');
        if (summary) summary.click();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
